import { FC } from 'react'

import { Page, StyleSheet, Text, View, Image } from '@react-pdf/renderer'

import Logo from 'src/Assets/logo2.png'
import { OfferProps } from 'src/Components/Offer/index.tsx'
import { numberToMoney } from 'src/Helpers/functions.tsx'
import { colors } from 'src/Helpers/constants.ts'
import DefaultEquipment from 'src/Assets/equipment.png'
import { VatVisibilityEnum } from 'src/Pages/Calculator/helpers.ts'
const styles = StyleSheet.create({
  page: {
    padding: 39,
    paddingLeft: 78,

    fontFamily: 'Geometria',
    display: 'flex',
    flexDirection: 'column',
    gap: 30,
  },
  offerNumber: {
    fontFamily: 'Geometria',
    fontSize: 10,
    fontStyle: 'italic',
  },
  client: {
    fontFamily: 'Geometria',
    fontSize: 12,
    fontWeight: 600,
  },
  descriptionWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    marginBottom: 25,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerRight: {
    textAlign: 'right',
  },
  title: {
    fontSize: 12,
    textAlign: 'center',
  },
  introText: {
    fontSize: 12,
    textAlign: 'justify',
  },
  table: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #eee',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    breakInside: false,
  },
  tableRowHeader: {
    backgroundColor: '#d9edf7',
  },
  tableCol: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
    borderRight: '1px solid #eee',
  },
  tableColHeader: {
    textAlign: 'center',
  },
  tableCol_1: { flex: 0.8 },
  tableCol_2: { flex: 1.5 },
  tableCol_3: { flex: 2.5 },
  tableCol_4: { flex: 0.6 },
  tableCol_5: { flex: 1.2 },
  tableCol_6: { flex: 1.2 },
  tableCell: {
    textAlign: 'center',
    fontSize: 9,
  },
  tableImage: {
    width: 40,
  },
  conditions: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  listBullet: {
    width: 10,
  },
  listText: {
    flex: 1,
    fontSize: 11,
    color: `#919191`,
  },
  footer: {
    marginTop: 50,
    textAlign: 'right',
  },
  logoImage: {
    width: 170,
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerText: {
    fontSize: 11,
  },
  address: {
    borderTop: '1px solid #eee',
  },
  addressSeparator: {
    width: 3,
    height: 55,
    backgroundColor: colors.mainBlue,
  },
  addressWrapper: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
  },
  horizontalLine: {
    width: '100%',
    height: 1,
    backgroundColor: colors.thirdGray,
  },
  horizontalLineBlue: {
    width: '100%',
    height: 1,
    backgroundColor: colors.mainBlue,
  },
  subheader: {
    fontSize: 16,
    fontWeight: 600,
    color: colors.mainBlue,
  },
})

type DescriptionAndEquipmentsPageProps = Pick<
  OfferProps,
  | 'offerNumber'
  | 'offerEquipments'
  | 'clientContactName'
  | 'clientContactNamePosition'
  | 'clientName'
  | 'clientDivision'
  | 'introductionText'
  | 'offerDate'
  | 'phone'
>

const TableHeader: FC<{ isVAT: boolean; isNoVat: boolean }> = ({
  isVAT,
  isNoVat,
}) => (
  <View style={[styles.tableRow, styles.tableRowHeader]}>
    <View style={[styles.tableCol, styles.tableCol_1]}>
      <Text style={styles.tableCell}>Фото</Text>
    </View>
    <View style={[styles.tableCol, styles.tableCol_2]}>
      <Text style={styles.tableCell}>Наименование</Text>
    </View>
    <View style={[styles.tableCol, styles.tableCol_3]}>
      <Text style={styles.tableCell}>Характеристики</Text>
    </View>
    <View style={[styles.tableCol, styles.tableCol_4]}>
      <Text style={styles.tableCell}>Кол-во</Text>
    </View>
    {!isVAT && (
      <View style={[styles.tableCol, styles.tableCol_6]}>
        <Text style={styles.tableCell}>Стоимость без НДС, руб/час</Text>
      </View>
    )}
    {!isNoVat && (
      <View style={[styles.tableCol, styles.tableCol_5]}>
        <Text style={styles.tableCell}>Стоимость с НДС, руб/час</Text>
      </View>
    )}
  </View>
)

interface EquipmentItem {
  imageUrl?: string
  name: string
  characteristics: string
  quantity: number
  priceWithoutVat?: string | number
  priceWithVat?: string | number
  vatVisibility?: string
}

const EquipmentRow: FC<{
  item: EquipmentItem
  isVAT: boolean
  isNoVat: boolean
}> = ({ item, isVAT, isNoVat }) => (
  <View style={styles.tableRow}>
    <View style={[styles.tableCol, styles.tableCol_1]}>
      <Image
        style={styles.tableImage}
        src={item.imageUrl || DefaultEquipment}
      />
    </View>
    <View style={[styles.tableCol, styles.tableCol_2]}>
      <Text style={styles.tableCell}>{item.name}</Text>
    </View>
    <View style={[styles.tableCol, styles.tableCol_3]}>
      <Text style={styles.tableCell}>{item.characteristics}</Text>
    </View>
    <View style={[styles.tableCol, styles.tableCol_4]}>
      <Text style={styles.tableCell}>{item.quantity}</Text>
    </View>
    {!isVAT && (
      <View style={[styles.tableCol, styles.tableCol_6]}>
        <Text style={styles.tableCell}>
          {numberToMoney(Number(item.priceWithoutVat))}
        </Text>
      </View>
    )}
    {!isNoVat && (
      <View style={[styles.tableCol, styles.tableCol_5]}>
        <Text style={styles.tableCell}>
          {numberToMoney(Number(item.priceWithVat))}
        </Text>
      </View>
    )}
  </View>
)

export const DescriptionAndEquipmentsPage: FC<
  DescriptionAndEquipmentsPageProps
> = ({
  offerNumber,
  offerEquipments = [],
  clientContactName,
  clientContactNamePosition,
  clientName,
  clientDivision,
  introductionText,
  offerDate,
  phone,
}) => {
  const vatVisibility = offerEquipments[0]?.vatVisibility
  const isVAT = vatVisibility === VatVisibilityEnum.VAT
  const isNoVat = vatVisibility === VatVisibilityEnum.NO_VAT

  const equipmentChunks = []
  for (let i = 0; i < offerEquipments.length; i += 7) {
    equipmentChunks.push(offerEquipments.slice(i, i + 7))
  }

  if (equipmentChunks.length <= 1) {
    return (
      <Page size='A4' style={styles.page}>
        <View style={styles.headerContainer}>
          <View>
            <Image src={Logo} style={styles.logoImage} />
          </View>
          <View style={styles.addressWrapper}>
            <View style={styles.addressSeparator}></View>
            <View>
              <Text style={styles.headerText}>АО «РУСНЕДРА МЕНЕДЖМЕНТ»</Text>
              <Text style={styles.headerText}>ул. Пискунова 160</Text>
              <Text style={styles.headerText}>{phone}</Text>
              <Text style={styles.headerText}>okta.com.ru</Text>
            </View>
          </View>
        </View>

        <View style={styles.horizontalLine}></View>

        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.offerNumber}>
              {` № ${offerNumber} от ${offerDate}г.`}
            </Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.client}>{clientContactNamePosition}</Text>
            <Text style={styles.client}>{clientName}</Text>
            <Text style={styles.client}>{clientContactName}</Text>
          </View>
        </View>
        <View style={styles.descriptionWrapper}>
          <Text style={styles.title}>Благодарим за обращение</Text>
          <Text style={styles.introText}>
            {`${introductionText} ${clientDivision}`}
          </Text>
        </View>

        <View style={styles.table}>
          <TableHeader isVAT={isVAT} isNoVat={isNoVat} />
          {offerEquipments.map((item, index) => (
            <EquipmentRow
              key={index}
              item={item}
              isVAT={isVAT}
              isNoVat={isNoVat}
            />
          ))}
        </View>

        <View style={styles.conditions}>
          <View>
            <View style={styles.listItem}>
              <Text style={styles.listText}>
                Техника не старше 3 лет и обновляется ежегодно
              </Text>
            </View>
            <View style={styles.listItem}>
              <Text style={styles.listText}>
                Оборудована системой ГЛОНАСС с wi-fi модулями
              </Text>
            </View>
            <View style={styles.listItem}>
              <Text style={styles.listText}>
                Двусторонними видеорегистраторами
              </Text>
            </View>
            <View style={styles.listItem}>
              <Text style={styles.listText}>
                Пакетом для эксплуатации в Северных районах.
              </Text>
            </View>
            <View style={styles.listItem}>
              <Text style={styles.listText}>
                Необходимыми разрешениями: ЧТО, ПТО, Ростехнадзор, ОПО, лицензия
                на пассажироперевозки, ДОПОГ и т.д.
              </Text>
            </View>
          </View>
        </View>
      </Page>
    )
  }

  return (
    <>
      <Page size='A4' style={styles.page}>
        <View style={styles.headerContainer}>
          <View>
            <Image src={Logo} style={styles.logoImage} />
          </View>
          <View style={styles.addressWrapper}>
            <View style={styles.addressSeparator}></View>
            <View>
              <Text style={styles.headerText}>АО «РУСНЕДРА МЕНЕДЖМЕНТ»</Text>
              <Text style={styles.headerText}>ул. Пискунова 160</Text>
              <Text style={styles.headerText}>{phone}</Text>
              <Text style={styles.headerText}>okta.com.ru</Text>
            </View>
          </View>
        </View>

        <View style={styles.horizontalLine}></View>

        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.offerNumber}>
              {` № ${offerNumber} от ${offerDate}г.`}
            </Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.client}>{clientContactNamePosition}</Text>
            <Text style={styles.client}>{clientName}</Text>
            <Text style={styles.client}>{clientContactName}</Text>
          </View>
        </View>
        <View style={styles.descriptionWrapper}>
          <Text style={styles.title}>Благодарим за обращение</Text>
          <Text style={styles.introText}>
            {`${introductionText} ${clientDivision}`}
          </Text>
        </View>

        <View style={styles.table}>
          <TableHeader isVAT={isVAT} isNoVat={isNoVat} />
          {equipmentChunks[0].map((item, index) => (
            <EquipmentRow
              key={index}
              item={item}
              isVAT={isVAT}
              isNoVat={isNoVat}
            />
          ))}
        </View>
      </Page>

      {equipmentChunks.slice(1).map((chunk, chunkIndex) => (
        <Page key={chunkIndex + 1} size='A4' style={styles.page}>
          <View style={styles.table}>
            <TableHeader isVAT={isVAT} isNoVat={isNoVat} />
            {chunk.map((item, index) => (
              <EquipmentRow
                key={index}
                item={item}
                isVAT={isVAT}
                isNoVat={isNoVat}
              />
            ))}
          </View>

          {chunkIndex === equipmentChunks.length - 2 && (
            <View style={styles.conditions}>
              <View>
                <View style={styles.listItem}>
                  <Text style={styles.listText}>
                    Техника не старше 3 лет и обновляется ежегодно
                  </Text>
                </View>
                <View style={styles.listItem}>
                  <Text style={styles.listText}>
                    Оборудована системой ГЛОНАСС с wi-fi модулями
                  </Text>
                </View>
                <View style={styles.listItem}>
                  <Text style={styles.listText}>
                    Двусторонними видеорегистраторами
                  </Text>
                </View>
                <View style={styles.listItem}>
                  <Text style={styles.listText}>
                    Пакетом для эксплуатации в Северных районах.
                  </Text>
                </View>
                <View style={styles.listItem}>
                  <Text style={styles.listText}>
                    Необходимыми разрешениями: ЧТО, ПТО, Ростехнадзор, ОПО,
                    лицензия на пассажироперевозки, ДОПОГ и т.д.
                  </Text>
                </View>
              </View>
            </View>
          )}
        </Page>
      ))}
    </>
  )
}
